<template>
  <div class="img-slider-section">
    <div ref="swiper" class="swiper">
      <div class="swiper-wrapper">
        <img-slide-parts
          v-for="(slide, index) in imgSlideLinks"
          :key="`${slide.img}-${index}`"
          :linkData="slide"
          class="swiper-slide"
        />
      </div>

      <div class="swiper-button-prev">
        <img :src="require('@/assets/img/icon/icon_swiper_prev.svg')" alt="" />
      </div>
      <div class="swiper-button-next">
        <img :src="require('@/assets/img/icon/icon_swiper_next.svg')" alt="" />
      </div>
      <div class="swiper-pagination"></div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import Swiper, { Navigation, Pagination } from 'swiper'
import ImgSlideParts from '@/components/HomePage/HomePane/parts/ImgSlideParts.vue'
import 'swiper/swiper-bundle.min.css'
import 'swiper/swiper.min.css'

/**
 * ヘッドラインのスライド画像の型定義
 */
export type ImgSliderLinkData = {
  /**
   * videoContentsId
   */
  videoContentsId: string
  /**
   * ヘッドラインカード選択時の画面遷移の方法。
   * SFgoの画面へ遷移する場合: router
   * SUPERFORMULA公式サイト、別サイトに遷移する場合: link
   */
  linkType: 'router' | 'link'
  /**
   * 遷移先のURL。または、パス
   * linkTypeがrouterの場合、パスが設定される。
   * 外部サイトの場合、URLが設定される。
   */
  link: string
  /**
   * ヘッドラインのサムネイル画像のURL
   */
  img: string
  /**
   * ヘッドラインに表示するテキスト
   */
  text: string
}

/**
 * HOME イメージスライダーセクション
 */
export default defineComponent({
  name: 'ImgSliderSection',
  components: {
    ImgSlideParts,
  },
  data(): {
    swiper: Swiper | null
  } {
    return {
      swiper: null,
    }
  },
  props: {
    imgSlideLinks: {
      type: Array as PropType<ImgSliderLinkData[]>,
      required: true,
    },
  },
  mounted() {
    // eslint-disable-next-line
    this.swiper = new Swiper(this.$refs.swiper as HTMLElement, {
      modules: [Navigation, Pagination],
      loop: true,
      spaceBetween: 0,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      pagination: {
        el: '.swiper-pagination',
      },
    })
  },
  watch: {
    imgSlideLinks(newVal: ImgSliderLinkData[], oldVal: ImgSliderLinkData[]) {
      const newValIds = newVal.map((val) => val.videoContentsId)
      const oldValIds = oldVal.map((val) => val.videoContentsId)
      if (newValIds !== oldValIds) {
        this.$nextTick(() => {
          if (this.swiper) {
            this.swiper.update()
            this.swiper.loopDestroy()
            this.swiper.loopCreate()
          }
        })
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.img-slider-section {
  .swiper {
    margin: 0 auto;
    padding-bottom: 18px;

    @media screen and (min-width: $responsiveTabletWidth - 1px) {
      padding-bottom: 16px;
    }

    &-slide {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    &-button-prev,
    &-button-next {
      width: auto;
      height: auto;

      img {
        width: 24px;
      }

      &::after {
        display: none;
      }
    }

    &-button-prev {
      top: calc(66 / 228) * 100%;
      left: 8px;
      margin-top: 0;
    }

    &-button-next {
      top: calc(66 / 228) * 100%;
      right: 8px;
      margin-top: 0;
    }

    &-pagination {
      bottom: 0;
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: center;
      gap: 5px;
    }

    @media screen and (min-width: $responsiveTabletWidth - 1px) {
      img {
        width: 36px;
      }

      &-button-prev {
        top: calc(112 / 316) * 100%;
        left: 16px;
      }

      &-button-next {
        top: calc(112 / 316) * 100%;
        right: 16px;
      }
    }
  }
}
</style>

<style lang="scss">
.img-slider-section {
  .swiper {
    &-pagination {
      &-bullet {
        width: 5px;
        height: 5px;
        margin: 0 !important;
        background: rgba(0, 0, 0, 0.2);
        opacity: 1;

        &-active {
          background: $black;
        }
      }
    }
  }
}
</style>
